import { css } from '@emotion/react';
import ClearIcon from '@mui/icons-material/Clear';
import { Chip, IconButton, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import { StandardFormatterId } from '../../../util/formatter-service';
import { fieldsConfigContainerCss, fieldsListContainerCss, fieldItemCss } from './data-display-common-styles';
import { DetailsFieldSelect } from './DetailsFieldSelect';
import { FieldFormatConfig, FormatterSelect } from '../../../components/FormatterSelect/FormatterSelect';
import { colors } from '../../../theme/colors';

const containerCss = css`
  ${fieldsConfigContainerCss};
  overflow: hidden;
  flex: 1;
  min-height: 0;
`;
const listContainerCss = css`
  ${fieldsListContainerCss};
  flex: 1;
  min-height: 0;
`;
const itemCss = css`
  ${fieldItemCss};
  grid-template-columns: 1fr 1fr auto;
`;

const labelCellCss = css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`;

export interface SelectableField {
  label?: string;
  key: string;
  isUniversal?: boolean;
  formatConfig?: FieldFormatConfig;
}

export interface IFieldSelectionConfigProps {
  onRemoveField: (key: string) => void;
  onAddField: (value: string) => void;
  onFormatChange?: (key: string, formatterId: StandardFormatterId | string | undefined) => void;
  selectedFields: string[];
  allFields: SelectableField[];
}

export function FieldSelectionConfig({
  onAddField,
  onRemoveField,
  selectedFields,
  allFields,
  onFormatChange,
}: IFieldSelectionConfigProps) {
  const fieldMap = useMemo(() => {
    return new Map(allFields.map((field) => [field.key, field]));
  }, [allFields]);

  const fieldOptions = useMemo(() => {
    const selectedSet = new Set(selectedFields);
    return allFields.reduce((acc, { label, key }) => {
      if (selectedSet.has(key)) {
        return acc;
      }
      acc.push({ displayName: label ?? key, value: key });
      return acc;
    }, [] as ISimpleChoice<string>[]);
  }, [allFields, selectedFields]);

  const items = useMemo(() => {
    return selectedFields.reduce((acc, key) => {
      const field = fieldMap.get(key);
      if (field) {
        acc.push(field);
      }
      return acc;
    }, [] as SelectableField[]);
  }, [selectedFields, fieldMap]);

  return (
    <Stack css={containerCss}>
      <Stack css={listContainerCss}>
        <Virtuoso
          data={items}
          style={{ flex: 1 }}
          itemContent={(_, field) => (
            <div css={itemCss}>
              <div css={labelCellCss}>
                <Typography variant='body2' noWrap minWidth='1rem'>
                  {field.label ?? field.key}
                </Typography>
                {field.isUniversal && (
                  <Chip label='Universal' color='secondary' size='small' variant='outlined' />
                )}
              </div>
              {field.formatConfig && onFormatChange && (
                <FormatterSelect
                  fieldKey={field.key}
                  renderer={field.formatConfig.renderer}
                  value={field.formatConfig.formatter}
                  onChange={(formatterId) => onFormatChange(field.key, formatterId)}
                />
              )}
              <IconButton
                onClick={() => onRemoveField(field.key)}
                sx={{ justifySelf: 'end', marginLeft: '0.5rem' }}
              >
                <ClearIcon htmlColor={colors.neutral[50]} fontSize='small' />
              </IconButton>
            </div>
          )}
        />
        <DetailsFieldSelect options={fieldOptions} onChange={onAddField} />
      </Stack>
    </Stack>
  );
}
